import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { collection, getDocs, updateDoc, doc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useCart } from '../context/CartContext';
import { soundEffects } from '../utils/soundEffects';
import ProductCard from '../components/ProductCard';
import Products2Hero from '../components/Products2Hero';
import { generateProductSlug } from '../utils/seoUtils';
import { Helmet } from 'react-helmet-async';
import './LaptopsPage.css'; // Keep this CSS import, as it seems to be shared

const DesktopsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { addToCart } = useCart();
  const [desktops, setDesktops] = useState([]);
  const [filteredDesktops, setFilteredDesktops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedBrand, setSelectedBrand] = useState('');

  // Filter states - updated for desktops
  const [filters, setFilters] = useState({
    availability: '',
    priceRange: '',
    brand: '',
    processor: '',
    generation: '',
    ram: '',
    hardDiskSize: '',
    category: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [sortBy, setSortBy] = useState('featured');

  useEffect(() => {
    fetchDesktops();
  }, []);

  useEffect(() => {
    // Check for category and brand parameters in URL
    const urlParams = new URLSearchParams(location.search);
    const categoryParam = urlParams.get('category');
    const brandParam = urlParams.get('brand');
    
    if (categoryParam) {
      setSelectedCategory(categoryParam);
      setFilters(prev => ({
        ...prev,
        category: categoryParam
      }));
    }
    
    if (brandParam) {
      setSelectedBrand(brandParam);
      setFilters(prev => ({
        ...prev,
        brand: brandParam
      }));
    }
  }, [location.search]);

  useEffect(() => {
    applyFilters();
  }, [desktops, filters, sortBy]);

  const fetchDesktops = async () => {
    try {
      setLoading(true);
      const querySnapshot = await getDocs(collection(db, 'products', 'desktops', 'items'));
      const desktopData = querySnapshot.docs.map((docSnapshot) => {
        const data = docSnapshot.data();
        const id = docSnapshot.id;
        const slug = generateProductSlug({ id, ...data });
        
        if (!data.slug || data.slug !== slug) {
          updateDoc(doc(db, 'products', 'desktops', 'items', id), { slug })
            .catch(err => console.warn('Desktop slug update failed:', err));
        }
        
        return { id, ...data, slug };
      });
      setDesktops(desktopData);
    } catch (err) {
      setError('Failed to load desktops');
      console.error('Error fetching desktops:', err);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...desktops];

    // Apply filters
    if (filters.category) {
      filtered = filtered.filter(desktop => desktop.category === filters.category);
    }
    if (filters.availability) {
      filtered = filtered.filter(desktop => desktop.availability === filters.availability);
    }
    if (filters.brand) {
      filtered = filtered.filter(desktop => desktop.brand === filters.brand);
    }
    if (filters.processor) {
      filtered = filtered.filter(desktop => desktop.processor.toLowerCase().includes(filters.processor.toLowerCase()));
    }
    if (filters.generation) {
      filtered = filtered.filter(desktop => desktop.generation === filters.generation);
    }
    if (filters.ram) {
      filtered = filtered.filter(desktop => desktop.ram === filters.ram);
    }
    if (filters.hardDiskSize) {
      filtered = filtered.filter(desktop => desktop.hardDiskSize === filters.hardDiskSize);
    }
    if (filters.priceRange) {
      const [min, max] = filters.priceRange.split('-').map(Number);
      filtered = filtered.filter(desktop => {
        const price = desktop.price;
        if (max) return price >= min && price <= max;
        return price >= min;
      });
    }

    // Apply sorting
    switch (sortBy) {
      case 'price-low':
        filtered.sort((a, b) => a.price - b.price);
        break;
      case 'price-high':
        filtered.sort((a, b) => b.price - a.price);
        break;
      case 'newest':
        filtered.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
        break;
      case 'discount':
        filtered.sort((a, b) => (b.discount || 0) - (a.discount || 0));
        break;
      default:
        // Featured - keep original order
        break;
    }
    setFilteredDesktops(filtered);
  };

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      availability: '',
      priceRange: '',
      brand: '',
      processor: '',
      generation: '',
      ram: '',
      hardDiskSize: '',
      category: ''
    });
    setSelectedCategory('');
    setSelectedBrand('');
    // Clear URL parameters
    navigate('/desktops', { replace: true });
  };

  const handleProductClick = (desktop) => {
    const slug = generateProductSlug(desktop);
    navigate(`/desktop/${slug || desktop.id}`);
  };

  const handleAddToCart = (product) => {
    const cartItem = {
      id: product.id,
      name: product.name,
      brand: product.brand,
      price: product.price,
      originalPrice: product.originalPrice,
      imageUrls: product.imageUrls, // Use imageUrls like LaptopsPage
      features: [
        product.processor,
        product.ram,
        product.hardDiskSize
      ].filter(Boolean),
      quantity: 1
    };
    addToCart(cartItem);

    // Play sound effect
    soundEffects.playAddToCartSound();
  };

  // Get unique filter options
  const getUniqueValues = (key) => {
    return [...new Set(desktops.map(desktop => desktop[key]).filter(Boolean))];
  };

  return (
    <div className="laptops-page">
      <Helmet>
        <title>Affordable Refurbished Desktops | Best Price Workstations | Nexawave</title>
        <meta name="description" content="Save big on high-performance refurbished desktops and workstations. Certified quality, best prices, and 12-month warranty. The most affordable professional desktops in Hyderabad." />
        <meta name="keywords" content="affordable desktops, refurbished workstations, best price computers, used desktops hyderabad, Nexawave" />
      </Helmet>
      <Products2Hero 
        className="hero-tight"
        tagline="PREMIUM PERFORMANCE · CERTIFIED REFURBISHED · 12-MONTH WARRANTY"
        title1="Desktops"
        title2=""
        title3=""
        description=""
        button1Text="Shop Desktops"
        button1Link="#laptops-collection"
      />
      <div className="container" id="laptops-collection">
        {loading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading desktops...</p>
          </div>
        ) : error ? (
          <div className="error-container">
            <p>{error}</p>
          </div>
        ) : (
          <>
            {/* Filter and Sort Section */}
            <div className="filter-section">
              <div className="filter-header">
                <div className="results-info">
                  <h2>
                    Showing {filteredDesktops.length} {selectedBrand ? selectedBrand : selectedCategory ? selectedCategory.toLowerCase() : ''} desktops
                  </h2>
                  <p>
                    {selectedBrand 
                      ? `Perfect ${selectedBrand} systems for your needs`
                      : selectedCategory 
                        ? `Perfect ${selectedCategory.toLowerCase()} systems for your needs`
                        : 'Find your perfect desktop system'
                    }
                  </p>
                </div>
                <div className="filter-controls">
                  <button 
                    className={`filter-toggle ${showFilters ? 'active' : ''}`}
                    onClick={() => setShowFilters(!showFilters)}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z"/>
                    </svg>
                    Filters
                  </button>
                  <select 
                    value={sortBy} 
                    onChange={(e) => setSortBy(e.target.value)}
                    className="sort-select"
                  >
                    <option value="featured">Featured</option>
                    <option value="price-low">Price: Low to High</option>
                    <option value="price-high">Price: High to Low</option>
                    <option value="newest">Newest First</option>
                    <option value="discount">Best Deals</option>
                  </select>
                </div>
              </div>
              {/* Filter Panel */}
              <div className={`filter-panel ${showFilters ? 'show' : ''}`}>
                <div className="filter-grid">
                  <div className="filter-group">
                    <label>Availability</label>
                    <select 
                      value={filters.availability} 
                      onChange={(e) => handleFilterChange('availability', e.target.value)}
                    >
                      <option value="">All Availability</option>
                      {getUniqueValues('availability').map(availability => (
                        <option key={availability} value={availability}>{availability}</option>
                      ))}
                    </select>
                  </div>
                  <div className="filter-group">
                    <label>Price Range</label>
                    <select 
                      value={filters.priceRange} 
                      onChange={(e) => handleFilterChange('priceRange', e.target.value)}
                    >
                      <option value="">All Prices</option>
                      <option value="0-30000">Under ₹30,000</option>
                      <option value="30000-50000">₹30,000 - ₹50,000</option>
                      <option value="50000-75000">₹50,000 - ₹75,000</option>
                      <option value="75000-100000">₹75,000 - ₹1,00,000</option>
                      <option value="100000">Above ₹1,00,000</option>
                    </select>
                  </div>
                  <div className="filter-group">
                    <label>Brand</label>
                    <select 
                      value={filters.brand} 
                      onChange={(e) => handleFilterChange('brand', e.target.value)}
                    >
                      <option value="">All Brands</option>
                      {getUniqueValues('brand').map(brand => (
                        <option key={brand} value={brand}>{brand}</option>
                      ))}
                    </select>
                  </div>
                  <div className="filter-group">
                    <label>Processor</label>
                    <select 
                      value={filters.processor} 
                      onChange={(e) => handleFilterChange('processor', e.target.value)}
                    >
                      <option value="">All Processors</option>
                      <option value="Intel Core i3">Intel Core i3</option>
                      <option value="Intel Core i5">Intel Core i5</option>
                      <option value="Intel Core i7">Intel Core i7</option>
                      <option value="AMD Ryzen">AMD Ryzen</option>
                    </select>
                  </div>
                  <div className="filter-group">
                    <label>Generation</label>
                    <select 
                      value={filters.generation} 
                      onChange={(e) => handleFilterChange('generation', e.target.value)}
                    >
                      <option value="">All Generations</option>
                      {getUniqueValues('generation').map(generation => (
                        <option key={generation} value={generation}>{generation}</option>
                      ))}
                    </select>
                  </div>
                  <div className="filter-group">
                    <label>RAM</label>
                    <select 
                      value={filters.ram} 
                      onChange={(e) => handleFilterChange('ram', e.target.value)}
                    >
                      <option value="">All RAM</option>
                      {getUniqueValues('ram').map(ram => ( 
                        <option key={ram} value={ram}>{ram}</option>
                      ))}
                    </select>
                  </div>
                  <div className="filter-group">
                    <label>Hard Disk Size</label>
                    <select 
                      value={filters.hardDiskSize} 
                      onChange={(e) => handleFilterChange('hardDiskSize', e.target.value)}
                    >
                      <option value="">All Storage</option>
                      {getUniqueValues('hardDiskSize').map(storage => (
                        <option key={storage} value={storage}>{storage}</option>
                      ))}
                    </select>
                  </div>
                  <div className="filter-actions">
                    <button onClick={clearFilters} className="clear-filters">
                      Clear All
                    </button>
                  </div>
                </div>
              </div>
            </div>
            {/* Products Grid */}
            <div className="laptops-grid">
              {filteredDesktops.map((desktop) => (
                <div key={desktop.id} className="laptop-card-wrapper">
                  <ProductCard
                    product={{
                      image: desktop.imageUrls?.[0] || '/api/placeholder/300/200',
                      imageAlt: desktop.name,
                      badge: desktop.availability === 'Available' ? 'In Stock' : 'Out of Stock',
                      isNew: false,
                      stockLeft: desktop.stockLeft,
                      brand: desktop.brand || 'NexaWave',
                      name: desktop.name,
                      subtitle: [desktop.condition, desktop.warranty].filter(Boolean).join(' | '),
                      scentNote: [desktop.processor, desktop.ram ? `${desktop.ram} RAM` : '', desktop.hardDiskSize].filter(Boolean).join(' | '),
                      rating: Number(desktop.rating) || 4.8,
                      reviews: Number(desktop.reviews) || 120,
                      originalPrice: Number(desktop.originalPrice) || Number(desktop.price) || 0,
                      salePrice: Number(desktop.price) || Number(desktop.originalPrice) || 0
                    }}
                    onCardClick={() => handleProductClick(desktop)}
                    onAddToCart={() => handleAddToCart(desktop)}
                    canAddToCart={desktop.availability === 'Available'}
                    showRating={false}
                    titleTwoLine={true}
                  />
                </div>
              ))}
            </div>
            {filteredDesktops.length === 0 && (
              <div className="no-results">
                <div className="no-results-icon">🔍</div>
                <h3>No desktops found</h3>
                <p>Try adjusting your filters to see more results</p>
                <button onClick={clearFilters} className="btn btn-primary">
                  Clear Filters
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
export default DesktopsPage;
